<script lang="ts" setup>
import { ref, onUnmounted, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/store/user'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { notificationError } from '@/app/core/util/notificationUtils'
import { useI18n } from 'vue-i18n'
import Popper from 'vue3-popper'
import {
  DBtn,
  DTextField,
  DTable,
  DModal,
  DLoading,
  SearchMdIcon,
  Loading02Icon,
  Edit03Icon,
  Trash03Icon,
  Lock03Icon,
  LockUnlocked03Icon
} from 'front-library'
import {
  generateReportList,
  getReportRequest
} from '@/app/core/services/reports'
import {
  getCostCenters,
  CostCenter,
  deleteCostCenter,
  getCompaniesAccountIntegration,
  resourceSync,
  getIntegrationAction,
  stateCostCenter
} from '@/app/core/services/cost-centers'
usePayrollChange(() => window.location.reload())

/* store, router, notifications */
const router = useRouter()
const store = useUserStore()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Modal y Loading */
const validSyncIntegrationSAP = ref(false)
const showModal = ref(false)
const titleModal = ref()
const descriptionModal = ref()
const typeAlertModal = ref('')
const acceptQuestion = ref('')

const loading = ref(false)
const messageLoading = ref('')

/* Datos */
const search = ref()
const tableCostCenter = ref(null)
const costCenterForDelete = ref()
const urlFile = ref(null)
const refresh = ref()
const payload = ref()
const refreshIntegrationSAP = ref()
const refreshSyncData = ref()
const isRefreshData = ref<boolean>(false)
const costCenterUpdateState = ref()
const isDelete = ref(false)

/* Datos componente table */
const columns = ref([
  { name: 'code', title: t('costCenters.list.code'), table: 'code' },
  { name: 'name', title: t('costCenters.list.name'), table: 'name' },
  { name: 'parent', title: t('costCenters.list.parent'), table: 'parent' },
  { name: 'state', title: t('costCenters.list.state'), table: 'is_inactive' },
  {
    name: 'updated_at',
    title: t('costCenters.list.lastUpdate'),
    table: 'updated_at'
  },
  { name: 'actions', title: '' }
])
const rows = ref<CostCenter[]>([])
const perPage = ref(0)
const offset = ref(0)
const totalRows = ref(0)
const sortBy = ref('')

/* Redirect's */
const createCostCenter = () => {
  void router.push({ name: 'CreateCostCenters' })
}
const updateCostCenter = (id: any) => {
  void router.push({ name: 'UpdateCostCenters', params: { id } })
}
const getCostCenter = (id: any) => {
  void router.push({ name: 'GetCostCenters', params: { id } })
}

const changeStateCostCenter = (values: any) => {
  const text = values.is_inactive
    ? t('table.action.activate')
    : t('table.action.deactivate')
  costCenterUpdateState.value = [
    { name: values.name, id: values.id, is_inactive: values.is_inactive }
  ]
  titleModal.value = t('costCenters.list.actionStatus', {
    status: text,
    costCenter: values.name as string
  })
  descriptionModal.value = ''
  typeAlertModal.value = 'question'
  acceptQuestion.value = t('modal.acceptQuestion', { action: text })
  showModal.value = true
  isDelete.value = false
}

const changeStateConfirm = async () => {
  loading.value = true
  messageLoading.value = ''
  showModal.value = false
  await stateCostCenter(
    costCenterUpdateState.value[0].id,
    !costCenterUpdateState.value[0].is_inactive
  )
    .then(response => {
      notifications.addNotification({
        title: !costCenterUpdateState.value[0].is_inactive
          ? t('notification.disabled')
          : t('notification.activated'),
        text: t('costCenters.notification.changeStatus', {
          costCenter: costCenterUpdateState.value[0].name as string,
          status: !costCenterUpdateState.value[0].is_inactive
            ? t('table.action.deactivated')
            : t('table.action.asset')
        }),
        type: 'success',
        time: 10000
      })
      void costCenters()
    })
    .catch(async (error: any) => {
      notificationError(error, 'costCenters', t, notifications, loading)
    })
}

/* Eliminar centro de costo */
const removeCostCenter = (values: any) => {
  titleModal.value = t('costCenters.list.actionStatus', {
    status: t('table.action.remove').toLowerCase(),
    costCenter: values.name as string
  })
  descriptionModal.value = t('modal.description')
  acceptQuestion.value = t('modal.acceptQuestion', {
    action: t('table.action.delete')
  })
  typeAlertModal.value = 'question'
  showModal.value = true
  costCenterForDelete.value = values.id
  isDelete.value = true
}
const deleteConfirm = async () => {
  loading.value = true
  messageLoading.value = ''
  showModal.value = false
  await deleteCostCenter(costCenterForDelete.value)
    .then(response => {
      notifications.addNotification({
        title: t('notification.removed'),
        text: t('costCenters.notification.action', [
          t('notification.wasRemoved')
        ]),
        type: 'success',
        time: 10000
      })
      void costCenters()
    })
    .catch(async (error: any) => {
      notificationError(error, 'costCenters', t, notifications, loading)
    })
}

/* Generar reporte */
const downloadList = async () => {
  try {
    loading.value = true
    messageLoading.value = t('loading.report')
    urlFile.value = null
    const data = {
      code: 'configurations/entity-table',
      parameters: {
        query: JSON.stringify(payload.value),
        fields: payload.value.fields.filter(
          (field: any) => !field.includes('id')
        ),
        headers: columns.value
          .filter((item: any) => item.title !== '')
          .map((item: any) => item.title),
        module: t('costCenters.list.title', 1)
      }
    }
    const id = await generateReportList(data)
    refresh.value = setInterval(async () => {
      const response = await getReportRequest(id)
      if (response.status === 'Completado') {
        urlFile.value = response.url
        descriptionModal.value = ''
        typeAlertModal.value = 'success'
        titleModal.value = t('reports.notification.reportGenerated')
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      } else if (response.status === 'Error') {
        typeAlertModal.value = 'error'
        titleModal.value = t('reports.notification.reportNoGenerated')
        descriptionModal.value = response.errors
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      }
    }, 5000)
  } catch (error: any) {
    notificationError(error, 'costCenters', t, notifications, loading)
  }
}

/* Cerrar el componente Modal */
const closeModal = () => {
  showModal.value = false
  urlFile.value = null
}

/* Listar centros de costo */
const costCenters = async () => {
  try {
    if (!validSyncIntegrationSAP.value) loading.value = true
    messageLoading.value = t('loading.data')
    isRefreshData.value = true
    const data: any = await getCostCenters(
      offset.value,
      perPage.value,
      sortBy.value,
      search.value === '' ? undefined : search.value
    )
    rows.value = data[0].results
    totalRows.value = data[0].totalItems
    payload.value = data[1]
    loading.value = false
    loading.value = false
    isRefreshData.value = false
  } catch (error: any) {
    notificationError(error, 'costCenters', t, notifications, loading)
  }
}

/* Enviar peticion de sincronizar */
const syncIntegrationSAP = async () => {
  loading.value = true
  messageLoading.value = t('loading.updating')
  try {
    const response = await resourceSync()
    loading.value = false
    integrationRequest(response)
  } catch (error: any) {
    notificationError(error, 'costCenters', t, notifications, loading)
  }
}

/* Mostrar estado del la solicitud de la integracion */
const integrationRequest = (id: string) => {
  refreshIntegrationSAP.value = setInterval(async () => {
    try {
      const response = await getIntegrationAction(id)
      if (response.status === 'Completado') {
        loading.value = false
        clearInterval(refreshIntegrationSAP.value)
        isRefreshData.value = false
      } else if (response.status === 'Error') {
        typeAlertModal.value = 'error'
        titleModal.value = t('costCenters.notification.errorSync')
        if (response.errors.length === 1) {
          descriptionModal.value = response.errors[0]
        } else {
          for (let i = 0; i < response.errors.length; i++) {
            const error = response.errors[i]
            descriptionModal.value += error
            if (i < response.errors.length - 1) {
              descriptionModal.value += '<br>'
            }
          }
        }
        showModal.value = true
        loading.value = false
        clearInterval(refreshIntegrationSAP.value)
      }
    } catch (error: any) {
      notificationError(error, 'costCenters', t, notifications, loading)
    }
  }, 5000)
}

onMounted(async () => {
  validSyncIntegrationSAP.value = await getCompaniesAccountIntegration()
  if (validSyncIntegrationSAP.value) {
    isRefreshData.value = true
    refreshSyncData.value = setInterval(() => {
      void costCenters()
    }, 10000)
  }
})

onUnmounted(() => {
  clearInterval(refreshSyncData.value)
  clearInterval(refreshIntegrationSAP.value)
  clearInterval(refresh.value)
})
</script>
<template>
  <DLoading v-show="loading" :message="messageLoading" />
  <DModal
    :open="showModal"
    :typeAlert="typeAlertModal"
    :title="titleModal"
    :description="descriptionModal"
    :acceptQuestion="acceptQuestion"
    @closeModal="closeModal"
    @otherMethod="isDelete ? deleteConfirm() : changeStateConfirm()">
    <template v-slot:description v-if="urlFile != null">
      <a
        class="underline tracking-widest text-blue-600"
        :href="urlFile"
        target="_blank"
        v-html="t('reports.notification.viewFile')" />
    </template>
  </DModal>
  <div class="h-full px-5 overflow-auto scroll">
    <div class="bg-gray-50 pb-4">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow"
          v-html="t('costCenters.list.title', 1)" />
        <p
          class="text-gray-500 text-shadow"
          v-html="t('costCenters.list.description')" />
      </div>
      <div class="sm:flex justify-between pt-8">
        <div class="flex mb-3 sm:mb-0 max-w-2xl sm:w-2/3 md:w-1/2 lg:w-5/12">
          <DTextField
            :placeholder="t('form.button.search') + '...'"
            :icon="'SearchLgIcon'"
            class="mr-2 w-full"
            v-model="search"
            @keyup.enter="costCenters" />
          <DBtn @click.prevent="costCenters" v-html="t('form.button.search')" />
        </div>
        <div class="flex gap-2">
          <!-- boton de sincronizar -->
          <DBtn
            v-if="validSyncIntegrationSAP"
            type="button"
            @click="syncIntegrationSAP()">
            {{ t('form.button.sync') }}
          </DBtn>
          <DBtn
            color="excel"
            type="button"
            @click="downloadList"
            v-html="t('form.button.download')" />
          <DBtn
            :icon="'PlusIcon'"
            type="button"
            @click="createCostCenter()"
            v-if="store.hasPermission('cost_center:create')">
            {{
              t('form.button.create', { form: t('costCenters.list.title', 0) })
            }}
          </DBtn>
        </div>
      </div>
    </div>
    <DTable
      ref="tableCostCenter"
      :columns="columns"
      :rows="rows"
      :total-rows="totalRows"
      :select-options="false"
      :text-no-data="t('table.noData')"
      :text-per-page="t('table.rowsPerPage')"
      @perPage=";(perPage = $event), costCenters()"
      @offset="offset = $event"
      @sort=";(sortBy = $event), costCenters()">
      <template #state="data: any">
        <div
          class="flex items-center rounded-full font-semibold w-fit text-sm py-1 px-3"
          :class="
            data.is_inactive
              ? 'bg-red-100 text-red-600'
              : 'bg-green-100 text-green-600'
          ">
          <div
            class="mr-2 w-1.5 h-1.5 rounded-full"
            :class="data.is_inactive ? 'bg-red-600' : 'bg-green-600'"></div>
          {{
            data.is_inactive
              ? t('table.action.inactive')
              : t('table.action.active')
          }}
        </div>
      </template>
      <template #actions="data: any">
        <div class="flex py-2.5 justify-end gap-1">
          <Popper
            v-if="store.hasPermission('cost_center:get')"
            arrow
            offsetDistance="12"
            :content="t('table.action.detail')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="getCostCenter(data.id)">
              <SearchMdIcon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="
              !data.is_inactive && store.hasPermission('cost_center:update')
            "
            arrow
            offsetDistance="12"
            :content="t('table.action.edit')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="updateCostCenter(data.id)">
              <Edit03Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="store.hasPermission('cost_center:enable')"
            arrow
            offsetDistance="12"
            :content="
              data.is_inactive
                ? t('companies.list.deactivate')
                : t('companies.list.activate')
            "
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="changeStateCostCenter(data)">
              <Lock03Icon
                v-if="data.is_inactive"
                class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
              <LockUnlocked03Icon
                v-else
                class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="store.hasPermission('cost_center:delete')"
            arrow
            offsetDistance="12"
            :content="t('table.action.remove')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="removeCostCenter(data)">
              <Trash03Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
        </div>
      </template>
      <template #refresh v-if="isRefreshData">
        <Loading02Icon class="rotate text-gray-400 mr-2" />
        <h1 class="text-gray-400 text-base" v-html="t('import.list.loading')" />
      </template>
    </DTable>
  </div>
</template>
